import { useState, useEffect, useCallback } from 'react';
import { adminProjectsApi, type SyntaxMonitorData } from '../../projects/admin-api';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'medium' });
}

function fmtDuration(ms: number | null | undefined): string {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return `${h}h ${remM}m`;
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.round(diff / 60)}min`;
  if (diff < 86400) return `hace ${Math.round(diff / 3600)}h`;
  return `hace ${Math.round(diff / 86400)}d`;
}

function coveragePct(count: number, expected: number): string {
  if (!expected) return '—';
  return `${Math.round((count / expected) * 100)}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function StatusDot({ health }: { health: string }): JSX.Element {
  const colors: Record<string, string> = {
    healthy: '#28a745',
    slow_but_active: '#ffc107',
    engine_offline: '#fd7e14',
    no_recent_contact: '#fd7e14',
    orphaned_run: '#dc3545',
    failed: '#dc3545',
  };
  return (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: colors[health] ?? '#adb5bd',
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  );
}

function Badge({ label, color }: { label: string; color: string }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        background: `${color}22`,
        color,
        border: `1px solid ${color}44`,
      }}
    >
      {label}
    </span>
  );
}

function analyticBadge(status: string): JSX.Element {
  const map: Record<string, { label: string; color: string }> = {
    syntax_ready:      { label: 'Lista', color: '#28a745' },
    syntax_processing: { label: 'Calculando', color: '#17a2b8' },
    syntax_queued:     { label: 'En cola', color: '#6610f2' },
    syntax_pending:    { label: 'Pendiente', color: '#6c757d' },
    syntax_failed:     { label: 'Falló', color: '#dc3545' },
    syntax_orphaned:   { label: 'Huérfano', color: '#fd7e14' },
    syntax_incomplete: { label: 'Incompleto', color: '#ffc107' },
  };
  const { label, color } = map[status] ?? { label: status, color: '#6c757d' };
  return <Badge label={label} color={color} />;
}

function MetricRow({
  label,
  count,
  expected,
}: {
  label: string;
  count: number;
  expected: number;
}): JSX.Element {
  const isComplete = count === expected && expected > 0;
  const isEmpty = count === 0;
  return (
    <tr>
      <td style={{ padding: '3px 8px 3px 0', fontSize: 12, color: 'var(--text-secondary, #666)' }}>
        {label}
      </td>
      <td
        style={{
          padding: '3px 8px',
          fontSize: 12,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          color: isComplete ? '#28a745' : isEmpty ? '#adb5bd' : '#fd7e14',
        }}
      >
        {count.toLocaleString('es-AR')}
      </td>
      <td style={{ padding: '3px 0 3px 8px', fontSize: 11, color: 'var(--text-tertiary, #999)' }}>
        {coveragePct(count, expected)}
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface SyntaxMonitorPanelProps {
  projectId: string;
  onRetrySyntax?: () => void;
  retrySyntaxing?: boolean;
}

export function SyntaxMonitorPanel({
  projectId,
  onRetrySyntax,
  retrySyntaxing = false,
}: SyntaxMonitorPanelProps): JSX.Element {
  const [data, setData] = useState<SyntaxMonitorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetch = useCallback(async () => {
    try {
      const result = await adminProjectsApi.getSyntaxMonitor(projectId);
      setData(result);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [projectId]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  // Auto-refresh cada 30s si está calculando
  useEffect(() => {
    const isCalculating =
      data?.project.syntaxStatus === 'processing' ||
      data?.project.syntaxStatus === 'queued';

    if (!isCalculating) return;

    const interval = setInterval(() => { void fetch(); }, 30_000);
    return () => clearInterval(interval);
  }, [data?.project.syntaxStatus, fetch]);

  if (loading) {
    return (
      <div style={{ padding: '12px 0', color: 'var(--text-tertiary, #999)', fontSize: 13 }}>
        Cargando monitor de sintaxis...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '12px 0', color: '#dc3545', fontSize: 13 }}>
        {error ?? 'Sin datos'}
      </div>
    );
  }

  const { project, network, syntaxRun, engine, queue, metricsCoverage, derivedStatus } = data;

  const isCalculating =
    project.syntaxStatus === 'processing' || project.syntaxStatus === 'queued';

  const canRetrySyntax =
    derivedStatus.recommendedAction === 'retry_syntax' && onRetrySyntax;

  // ── Labels
  const networkLabel =
    project.urbanNetworkStatus === 'ready' ? 'Red lista' :
    project.urbanNetworkStatus === 'failed' ? 'Red falló' : 'Red pendiente';

  const actionLabels: Record<string, string> = {
    none:               'Ninguna',
    wait:               'Esperar',
    retry_syntax:       'Reintentar sintaxis',
    start_engine:       'Iniciar engine',
    clean_orphaned_run: 'Limpiar run huérfano',
    inspect_logs:       'Inspeccionar logs',
  };

  // ── Styles
  const panelStyle: React.CSSProperties = {
    borderTop: '1px solid var(--border-color, #e0e0e0)',
    paddingTop: 12,
    marginTop: 12,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: 14,
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '4px 16px',
    fontSize: 12,
  };

  const labelStyle: React.CSSProperties = {
    color: 'var(--text-tertiary, #999)',
    fontSize: 11,
  };

  const valueStyle: React.CSSProperties = {
    color: 'var(--text-primary, #333)',
    fontSize: 12,
    fontWeight: 500,
  };

  return (
    <div style={panelStyle}>
      {/* ── Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <StatusDot health={derivedStatus.health} />
          Monitor de Sintaxis Espacial
        </h4>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isCalculating && (
            <span style={{ fontSize: 11, color: '#17a2b8' }}>
              ↻ Auto-refresh 30s
            </span>
          )}
          <span style={{ fontSize: 11, color: 'var(--text-tertiary, #999)' }}>
            {timeAgo(lastRefresh.toISOString())}
          </span>
          <button
            type="button"
            onClick={() => { void fetch(); }}
            style={{
              fontSize: 11,
              padding: '2px 8px',
              border: '1px solid var(--border-color, #ddd)',
              borderRadius: 4,
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-secondary, #666)',
            }}
          >
            Actualizar
          </button>
        </div>
      </div>

      {/* ── Estado general */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        {/* Operativo */}
        <div style={{ ...sectionStyle, padding: '10px 12px', background: 'var(--bg-secondary, #f8f9fa)', borderRadius: 6 }}>
          <div style={{ ...labelStyle, marginBottom: 4 }}>Estado operativo</div>
          <div style={{ fontWeight: 600, fontSize: 13, color: project.urbanNetworkStatus === 'ready' ? '#28a745' : '#dc3545' }}>
            {networkLabel}
          </div>
          <div style={{ ...gridStyle, marginTop: 6 }}>
            <span style={labelStyle}>Edges</span>
            <span style={valueStyle}>{network.canonicalEdgesCount.toLocaleString('es-AR')}</span>
            <span style={labelStyle}>Nodos</span>
            <span style={valueStyle}>{network.canonicalNodesCount.toLocaleString('es-AR')}</span>
          </div>
        </div>

        {/* Analítico */}
        <div style={{ ...sectionStyle, padding: '10px 12px', background: 'var(--bg-secondary, #f8f9fa)', borderRadius: 6 }}>
          <div style={{ ...labelStyle, marginBottom: 4 }}>Estado analítico</div>
          <div style={{ marginBottom: 4 }}>{analyticBadge(derivedStatus.analyticStatus)}</div>
          <div style={{ ...gridStyle, marginTop: 6 }}>
            <span style={labelStyle}>Versión</span>
            <span style={valueStyle}>v{project.syntaxVersion}</span>
            <span style={labelStyle}>Calculado</span>
            <span style={valueStyle}>{fmtDate(project.syntaxCalculatedAt)}</span>
          </div>
        </div>
      </div>

      {/* ── Engine */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary, #555)' }}>
          Engine Python
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
          <div>
            <span style={labelStyle}>Estado: </span>
            <span style={{ color: engine.available ? '#28a745' : '#dc3545', fontWeight: 600 }}>
              {engine.available ? 'Activo' : 'Sin respuesta'}
            </span>
          </div>
          {engine.available && engine.jobStatus && (
            <>
              <div>
                <span style={labelStyle}>Job: </span>
                <span style={valueStyle}>{engine.jobStatus}</span>
              </div>
              {engine.jobProgress !== null && (
                <div>
                  <span style={labelStyle}>Progreso: </span>
                  <span style={valueStyle}>{engine.jobProgress}%</span>
                </div>
              )}
              {engine.jobCurrentPhase && (
                <div>
                  <span style={labelStyle}>Fase: </span>
                  <span style={valueStyle}>{engine.jobCurrentPhase}</span>
                </div>
              )}
            </>
          )}
          <div>
            <span style={labelStyle}>Consultado: </span>
            <span style={valueStyle}>{timeAgo(engine.checkedAt)}</span>
          </div>
        </div>
        {engine.jobError && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#dc3545' }}>
            Error engine: {engine.jobError}
          </div>
        )}
      </div>

      {/* ── Run activo */}
      {syntaxRun && (
        <div style={sectionStyle}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary, #555)' }}>
            Último run
          </div>
          <div style={{ ...gridStyle, gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div>
              <div style={labelStyle}>Estado</div>
              <div style={valueStyle}>{syntaxRun.status}</div>
            </div>
            <div>
              <div style={labelStyle}>Engine status</div>
              <div style={valueStyle}>{syntaxRun.engineStatus ?? '—'}</div>
            </div>
            <div>
              <div style={labelStyle}>Monitor fails</div>
              <div style={{ ...valueStyle, color: (syntaxRun.monitorFailures ?? 0) > 3 ? '#fd7e14' : 'inherit' }}>
                {syntaxRun.monitorFailures ?? 0}
              </div>
            </div>
            <div>
              <div style={labelStyle}>Iniciado</div>
              <div style={valueStyle}>{fmtDate(syntaxRun.startedAt)}</div>
            </div>
            <div>
              <div style={labelStyle}>Completado</div>
              <div style={valueStyle}>{fmtDate(syntaxRun.completedAt)}</div>
            </div>
            <div>
              <div style={labelStyle}>Duración</div>
              <div style={valueStyle}>{fmtDuration(syntaxRun.computationTimeMs)}</div>
            </div>
            {syntaxRun.engineJobId && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={labelStyle}>Engine Job ID</div>
                <div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                  {syntaxRun.engineJobId}
                </div>
              </div>
            )}
          </div>
          {syntaxRun.errorMessage && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: '#dc354511', border: '1px solid #dc354533', borderRadius: 4, fontSize: 11, color: '#dc3545' }}>
              {syntaxRun.errorMessage}
            </div>
          )}
        </div>
      )}

      {/* ── Cola Bull */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary, #555)' }}>
          Cola Bull (syntax)
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'Activos', count: queue.activeCount, warn: queue.activeCount > 1 },
            { label: 'En espera', count: queue.waitingCount, warn: false },
            { label: 'Fallidos', count: queue.failedCount, warn: queue.failedCount > 0 },
            { label: 'Completados', count: queue.completedCount, warn: false },
          ].map(({ label, count, warn }) => (
            <div key={label}>
              <span style={labelStyle}>{label}: </span>
              <span style={{ fontWeight: 600, color: warn ? '#fd7e14' : 'var(--text-primary, #333)' }}>
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Cobertura de métricas */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary, #555)' }}>
          Cobertura de métricas
          <Badge
            label={metricsCoverage.isComplete ? `${metricsCoverage.coveragePercent}% completo` : `${metricsCoverage.coveragePercent}%`}
            color={metricsCoverage.isComplete ? '#28a745' : metricsCoverage.coveragePercent > 0 ? '#ffc107' : '#adb5bd'}
          />
          <span style={labelStyle}>Esperado: {metricsCoverage.expectedEdges.toLocaleString('es-AR')} edges</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 24px' }}>
          {/* Global */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary, #999)', marginBottom: 2 }}>GLOBAL</div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                <MetricRow label="Integration (NC)" count={metricsCoverage.ncGlobal} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="NAIN" count={metricsCoverage.nainGlobal} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="Choice" count={metricsCoverage.choiceGlobal} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="NACH" count={metricsCoverage.nachGlobal} expected={metricsCoverage.expectedEdges} />
              </tbody>
            </table>
          </div>
          {/* r1200 */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary, #999)', marginBottom: 2 }}>R1200m</div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                <MetricRow label="Integration (NC)" count={metricsCoverage.ncR1200} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="NAIN" count={metricsCoverage.nainR1200} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="Choice" count={metricsCoverage.choiceR1200} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="NACH" count={metricsCoverage.nachR1200} expected={metricsCoverage.expectedEdges} />
              </tbody>
            </table>
          </div>
          {/* r800 */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary, #999)', marginBottom: 2 }}>R800m</div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                <MetricRow label="Integration (NC)" count={metricsCoverage.ncR800} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="NAIN" count={metricsCoverage.nainR800} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="Choice" count={metricsCoverage.choiceR800} expected={metricsCoverage.expectedEdges} />
                <MetricRow label="NACH" count={metricsCoverage.nachR800} expected={metricsCoverage.expectedEdges} />
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Acción recomendada + Botones */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 10,
        borderTop: '1px solid var(--border-color, #eee)',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <div style={{ fontSize: 12 }}>
          <span style={labelStyle}>Acción recomendada: </span>
          <span style={{ fontWeight: 600, color: derivedStatus.recommendedAction === 'none' ? '#28a745' : '#fd7e14' }}>
            {actionLabels[derivedStatus.recommendedAction] ?? derivedStatus.recommendedAction}
          </span>
          {project.pipelineError && (
            <div style={{ fontSize: 11, color: '#dc3545', marginTop: 4 }}>
              Pipeline error: {project.pipelineError}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {canRetrySyntax && (
            <button
              type="button"
              onClick={onRetrySyntax}
              disabled={retrySyntaxing}
              style={{
                fontSize: 12,
                padding: '4px 12px',
                borderRadius: 4,
                border: '1px solid #17a2b8',
                background: retrySyntaxing ? '#e9ecef' : '#17a2b822',
                color: '#17a2b8',
                cursor: retrySyntaxing ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              {retrySyntaxing ? 'Lanzando...' : 'Reintentar Sintaxis'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
